import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  setMobileHelpersData,
  setModalsData,
  TMobileHelpers,
  TModalsState,
} from "../api/store/commonSlice";
import svgIcons from "../assets/svg/svgIcons";
import {
  TDashboardMenuItem,
  useAppMenus,
} from "../navigation/dashboardSideMenu";
import { routeUrls } from "../navigation/routeUrls";
import { useLoggedInUserPerms } from "../utils/user";
import { APP_CONFIG } from "../constants/constants";
import { useThemeAssets } from "./common/ThemeContext";

type TDashboardMenuListItemProps = {
  dashboardMenuItem: TDashboardMenuItem;
  isSubmenu?: boolean;
};

const notActiveItemsClass = "opacity-70 hover:opacity-100";

const DashboardMenuListItem: React.FC<TDashboardMenuListItemProps> = ({
  dashboardMenuItem,
  isSubmenu = false,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard.sidemenu",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const urlParams = useParams();
  const { pathname } = useLocation();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const adminsMenuActive =
    pathname.indexOf(routeUrls.dashboardChildren.admins) > -1;
  const handleClick = (path: string) => {
    navigate(path);
    hideMobileMenu();
  };

  // mobile helpers
  const mobileHelpers = useSelector(
    (state: { commonReducer: { mobileHelpers: TMobileHelpers } }) =>
      state.commonReducer.mobileHelpers
  );

  const hideMobileMenu = () => {
    dispatch(
      setMobileHelpersData({
        ...mobileHelpers,
        showLeftMenuDrawer: false,
      })
    );
  };

  // Check if the menu item is active
  const active =
    !!dashboardMenuItem.children || dashboardMenuItem.hasQueryParam
      ? pathname.includes(dashboardMenuItem.path) // Submenu items
      : pathname === dashboardMenuItem.path || // Main menu items
        (pathname.includes(routeUrls.dashboardChildren.admins) &&
          pathname.includes(dashboardMenuItem.path)) || // Admins submenu items
        (pathname.includes(routeUrls.dashboardChildren.reports) &&
          pathname.includes(dashboardMenuItem.path)) || // Reports submenu items
        (pathname.includes(routeUrls.dashboardChildren.maintenance) &&
          pathname.includes(dashboardMenuItem.path)) || // Maintenance pages
        (pathname.includes(routeUrls.dashboardChildren.camera_admin) &&
          pathname.includes(dashboardMenuItem.path)) || // Cameras Admin (VisionMax)
        (pathname.includes(routeUrls.sysadminChildren.enterprises) &&
          pathname.includes(dashboardMenuItem.path)); // Enterprise admin pages

  const title = t(dashboardMenuItem.slug);

  // Display submenu items
  if (isSubmenu) {
    if (dashboardMenuItem.hasQueryParam) {
      // display item only if query param is present
      if (
        pathname.includes(
          `${dashboardMenuItem.path}/${urlParams[dashboardMenuItem.hasQueryParam]}`
        )
      ) {
        return (
          <li className="">
            <div
              className={`${modalsState.isExpandedSideMenu ? "" : "w-full flex justify-center"} px-3 py-2 rounded-lg ${
                active
                  ? "font-bold text-accent-blue-dark pointer-events-none touch-none"
                  : `font-semibold text-sidemenu-text ${notActiveItemsClass} hover:bg-gray-200 cursor-pointer`
              }`}
            >
              <Link
                to={`${dashboardMenuItem.path}/${urlParams[dashboardMenuItem.hasQueryParam]}`}
                onClick={
                  active ? undefined : () => handleClick(dashboardMenuItem.path)
                }
              >
                <span className="flex items-center gap-3">
                  <p className={`text-xs`}>{title}</p>
                </span>
              </Link>
            </div>
          </li>
        );
      } else {
        return <></>;
      }
    }

    // display submenu items
    return (
      <li className="">
        <div
          className={`${modalsState.isExpandedSideMenu ? "" : "w-full flex justify-center"} px-3 py-2 rounded-lg ${
            active
              ? "font-bold text-accent-blue-dark pointer-events-none touch-none"
              : `font-semibold text-sidemenu-text ${notActiveItemsClass} hover:bg-gray-200 cursor-pointer`
          }`}
        >
          <Link
            to={dashboardMenuItem.path}
            onClick={() => handleClick(dashboardMenuItem.path)}
          >
            <span className="flex items-center gap-3">
              <p className={`text-xs`}>{title}</p>
            </span>
          </Link>
        </div>
      </li>
    );
  }

  // Display main menu items
  return (
    <>
      <li className="">
        <div
          className={`w-full px-3 py-2 rounded-lg ring-offset-0 focus:ring-inset focus:ring-offset-accent-blue-dark ${
            active
              ? // CSS class `colourize-img-accent-dark` defined in src/index.scss
                // only for main menu items - pointer-events-none and touch-none disabled to allow clicking on the "Reports" main menu (from a report details page) as requested by client
                "bg-gray-300 colourize-img-accent-dark Xpointer-events-none Xtouch-none"
              : `hover:bg-gray-300 ${notActiveItemsClass} cursor-pointer`
          }`}
        >
          <Link
            to={dashboardMenuItem.path}
            className=""
            onClick={() => handleClick(dashboardMenuItem.path)}
          >
            <span
              className={`flex items-center ${adminsMenuActive ? "gap-1" : "gap-3"} ${!modalsState.isExpandedSideMenu ? "flex-col" : "flex-row"}`}
            >
              <img
                src={dashboardMenuItem.icon}
                alt={title}
                className="size-6 min-w-7"
              />
              <p
                className={`text-center text-xs ${active ? "font-bold text-accent-blue-dark" : "font-semibold text-sidemenu-text X-my-2"}`}
              >
                {/* -my-2 disabled as this is causing issues with active/inactive state */}
                {!modalsState.isExpandedSideMenu
                  ? title.split(" ").map((word, index) => (
                      <span key={index}>
                        {word}
                        <br />
                      </span> // Force each word onto a new line
                    ))
                  : title}
              </p>
            </span>
          </Link>
        </div>
      </li>
      {dashboardMenuItem.children && active && (
        <ul className="space-y-2 pb-4">
          {dashboardMenuItem.children.map((child, index) => (
            <DashboardMenuListItem
              key={index}
              dashboardMenuItem={child}
              isSubmenu={true}
            />
          ))}
        </ul>
      )}
    </>
  );
};

const Sidemenu = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard.sidemenu",
  });
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const adminsMenuActive =
    pathname.indexOf(routeUrls.dashboardChildren.admins) > -1;
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const { assets } = useThemeAssets();
  const dispatch = useDispatch();

  // mobile helpers
  const mobileHelpers = useSelector(
    (state: { commonReducer: { mobileHelpers: TMobileHelpers } }) =>
      state.commonReducer.mobileHelpers
  );

  const { loggedInUserIsSysAdmin } = useLoggedInUserPerms();

  const { dashboardMenuItems, dashboardAdminsMenuItems, sysadminMenuItems } =
    useAppMenus();

  const backToMainMenu = () => {
    navigate(routeUrls.dashboardChildren.overview);
  };

  const handleSideBarCollapsed = () => {
    dispatch(
      setModalsData({
        ...modalsState,
        isExpandedSideMenu: !modalsState.isExpandedSideMenu,
      })
    );
  };

  const hideMobileMenu = () => {
    dispatch(
      setMobileHelpersData({
        ...mobileHelpers,
        showLeftMenuDrawer: false,
      })
    );
  };

  return (
    <div
      className={`no-print flex flex-col bg-gray-100 py-4 ${
        modalsState.isExpandedSideMenu ? "px-4" : "px-3"
      } h-full gap-6`}
    >
      <Link
        to={routeUrls.dashboardChildren.overview}
        className={
          !modalsState?.isExpandedSideMenu
            ? assets?.logoUrl
              ? "flex min-h-[32px] min-w-[28px] overflow-hidden self-center"
              : "flex h-[32px] min-h-[32px] w-[28px] overflow-hidden self-center"
            : ""
        }
        onClick={hideMobileMenu}
      >
        <img
          src={assets?.logoUrl || svgIcons.Logo}
          className={
            !modalsState?.isExpandedSideMenu
              ? assets?.logoUrl
                ? "h-auto w-auto"
                : "h-full w-auto max-w-none"
              : ""
          }
          alt="fleet-track-logo"
        />
      </Link>
      {adminsMenuActive ? (
        <>
          <button
            type="button"
            className="flex items-center justify-center bg-accent-blue-pale hover:bg-accent-blue-paleO66 text-sidemenu-text-active font-medium text-base leading-6 py-1 rounded-lg w-full"
            onClick={backToMainMenu}
          >
            <img
              src={svgIcons.LeftArrowIcon}
              alt="left-arrow-icon"
              className={`size-10 p-2 rounded-full pointer-events-none${modalsState?.isExpandedSideMenu ? " -ml-4" : ""}`}
            />
            {modalsState.isExpandedSideMenu && t("main_menu")}
          </button>
          <p
            className={`text-accent-blue-dark ${!modalsState.isExpandedSideMenu ? "text-[10px]" : "text-sm"} font-bold uppercase leading-6 tracking-wider text-center`}
          >
            {t("administration")}
          </p>
          <ul className="space-y-4 flex-grow p-1 overflow-y-auto">
            {dashboardAdminsMenuItems.map((item, index) => (
              <DashboardMenuListItem
                key={index}
                dashboardMenuItem={{ ...item, slug: `admins.${item.slug}` }}
              />
            ))}
          </ul>
        </>
      ) : (
        <>
          <ul className="space-y-4 flex-grow p-1 overflow-y-auto">
            {(loggedInUserIsSysAdmin() === true
              ? [
                  ...sysadminMenuItems,
                  ...(APP_CONFIG.SYSADMIN_SUPERADMIN_USER_CAN_ACCESS_ALL_APP_FEATURES
                    ? dashboardMenuItems
                    : []),
                ]
              : dashboardMenuItems
            ).map((item, index) => (
              <DashboardMenuListItem key={index} dashboardMenuItem={item} />
            ))}
          </ul>
        </>
      )}

      {/* Expand / Collapse Arrow Button */}
      <button
        type="button"
        className={`${modalsState.isExpandedSideMenu ? "-mr-4" : "-mr-3"} hidden lg:flex items-center justify-center bg-accent-blue-pale hover:bg-accent-blue-paleO66 text-sidemenu-text-active font-medium text-base leading-6 place-self-end rounded-l-full w-14`}
        onClick={handleSideBarCollapsed}
      >
        <img
          src={
            modalsState.isExpandedSideMenu
              ? svgIcons.LeftArrowIcon
              : svgIcons.RightArrowIcon
          }
          alt="left-arrow-icon"
          className="size-10 p-2 rounded-full pointer-events-none"
        />
      </button>
    </div>
  );
};

export default Sidemenu;
